import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaClock, FaMicrophone, FaCheckCircle, FaTimes, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';

const PodCastHistory = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/podcast/booking/my-bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setBookings(data.data.bookings || []);
      } else {
        toast.error(data.message || 'Failed to fetch bookings');
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: FaClock, text: 'Pending' },
      confirmed: { color: 'bg-green-100 text-green-800', icon: FaCheckCircle, text: 'Confirmed' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: FaTimes, text: 'Cancelled' },
      completed: { color: 'bg-blue-100 text-blue-800', icon: FaCheckCircle, text: 'Completed' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-2" />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your bookings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
          <FaMicrophone className="mr-3 text-purple-600" />
          My Podcast Bookings
        </h1>
        <p className="text-gray-600">Track your podcast booking requests and their status</p>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-12">
          <FaMicrophone className="mx-auto text-gray-400 text-4xl mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
          <p className="text-gray-600">You haven't made any podcast bookings yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {bookings.map((booking) => (
            <div key={booking._id} className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">
                        {booking.podcastTitle}
                      </h3>
                      <p className="text-gray-600 mb-2">{booking.topic}</p>
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <FaCalendarAlt className="w-4 h-4 mr-2" />
                        <span>{formatDate(booking.date)}</span>
                        <FaClock className="w-4 h-4 ml-4 mr-2" />
                        <span>{formatTime(booking.startTime)} - {formatTime(booking.endTime)}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      {getStatusBadge(booking.status)}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Contact Person:</span>
                      <span className="ml-2 text-gray-900">{booking.contactPerson}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Email:</span>
                      <span className="ml-2 text-gray-900">{booking.contactEmail}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Phone:</span>
                      <span className="ml-2 text-gray-900">{booking.contactPhone}</span>
                    </div>
                    {booking.companyName && (
                      <div>
                        <span className="font-medium text-gray-700">Company:</span>
                        <span className="ml-2 text-gray-900">{booking.companyName}</span>
                      </div>
                    )}
                  </div>

                  {booking.adminNotes && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Admin Notes:</h4>
                      <p className="text-sm text-gray-600">{booking.adminNotes}</p>
                    </div>
                  )}

                  {booking.rejectionReason && (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                      <h4 className="text-sm font-medium text-red-700 mb-1">Rejection Reason:</h4>
                      <p className="text-sm text-red-600">{booking.rejectionReason}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PodCastHistory;